/**
 * /servere — migrated from public/js/servers.js (983 linii, cea mai mare).
 *
 * Enterprise filter UI pentru lista servere:
 *  - 4 noUiSlider (price, cores, mem, storage)
 *  - Checkboxes (comp_type, dep_type, location, hd_type, storage_type, speed)
 *  - Radio (period m/h)
 *  - Sort dropdown (asc/desc)
 *  - Pagination + swipe nav
 *  - Reset filters
 *  - Sticky menu + bookmark scrollspy (shared)
 *  - Mobile filter popup
 *  - Swiper testimonials
 *  - XML data fetch + parse → render cards via El() VDOM helper
 *
 * NOTA: tipuri TS sunt liberale (`any`/`unknown` in locuri complexe) deoarece
 * structura XML + dinamicul render-ului ar fi prea costisitor de tipat strict
 * pentru valoare echivalenta. Behavior 1:1 cu legacy.
 */
import { Resort } from '../core/resort.js';
import { initStickyBookmarks } from '../shared/sticky-bookmarks.js';
import { initWhenVisible } from '../shared/in-viewport.js';

declare const noUiSlider: {
    create: (target: Element, opts: unknown) => { on: (event: string, cb: (v: number[]) => void) => void; set: (v: number[]) => void };
};
declare const Swiper: new (selector: string, opts: unknown) => unknown;

/** Mini VDOM helper — same pattern ca-n domain-ext.ts */
function El(tag: string, attr: Record<string, string | null> = {}, ...children: unknown[]): HTMLElement {
    const elem = document.createElement(tag);
    for (const name in attr) {
        const v = attr[name];
        if (v === null || v === undefined) continue;
        elem.setAttribute(name, v);
    }
    children.forEach((c) => { if (c) elem.append(c as Node); });
    return elem;
}
// Expune El global pentru codul legacy compat (era window.El in original)
(window as unknown as { El: typeof El }).El = El;

interface ServerItem {
    type:     string;
    brand:    string;
    location: string;
    name:     string;
    cpu:      Array<{ vendor: string; name: string; freq: string; cores: number; threads?: number }>;
    gpu:      boolean;
    ram:      Array<{ size: number; type: string; freq?: number; spec?: string }>;
    hdd:      Array<{ size: number; type: string; count: number | string }>;
    net:      { speed?: number; bw?: string };
    price:    { m?: string; h?: string; discount?: string };
    orderUrl: string;
    depTime?: string;
    optional?: { ipPrice?: string; cpanelPrice?: string; raid?: string | false; bgp?: string | false };
}

interface FilterVars {
    comp_type:    string[];
    dep_type:     string[];
    location:     string[];
    price:        [number, number];
    period:       'm' | 'h';
    cores:        [number, number];
    mem:          [number, number];
    storage:      [number, number];
    hd_type:      string[];
    inc_gpu:      boolean;
    speed:        number[];
    inc_raid:     boolean;
    storage_type: string[];
    storage_count:number;
    sort:         'a' | 'd';
    page:         number;
}

interface Locale {
    period:   { m: string; h: string };
    included: string;
    ip_addr:  string;
    depTime:  { m: string; h: string };
    config:   string;
}

new Resort(() => {
    const defaultLang = 'ro';
    const maxRpp = 5;

    const config = {
        price:   [5, 20, 50, 100, 150, 200, 250, 300, 500, 700, 1000],
        cores:   [1, 2, 4, 8, 10, 12, 16, 20, 24, 28, 32, 48, 56, 64],
        mem:     [2, 4, 8, 16, 32, 64, 128, 256, 512],
        storage: [40, 80, 120, 160, 250, 320, 500, 750, 1024, 2048, 3072, 4096],
    };

    const defaults: FilterVars = {
        comp_type:    ['vps', 'dedicated'],
        dep_type:     ['instant', 'custom'],
        location:     ['ro', 'nl'],
        price:        [5, 1000],
        period:       'm',
        cores:        [1, 64],
        mem:          [2, 512],
        storage:      [40, 4096],
        hd_type:      ['intel', 'amd'],
        inc_gpu:      false,
        speed:        [1, 10],
        inc_raid:     false,
        storage_type: ['ssd', 'ssd_nvme', 'sata', 'sas', 'cloud'],
        storage_count: 1,
        sort:         'a',
        page:         1,
    };

    let vars: FilterVars = { ...defaults };
    let currency = '';
    let locale: Locale = {} as Locale;
    let servers: ServerItem[] = [];
    let setPage = false;

    const idxOrDefault = (arr: number[], val: number, fallback: number): number => {
        const i = arr.indexOf(val);
        return i === -1 ? fallback : i;
    };

    const _price = [
        idxOrDefault(config.price, defaults.price[0], 0),
        idxOrDefault(config.price, defaults.price[1], config.price.length - 1),
    ];
    const _cores = [
        idxOrDefault(config.cores, defaults.cores[0], 0),
        idxOrDefault(config.cores, defaults.cores[1], config.cores.length - 1),
    ];
    const _mem = [
        idxOrDefault(config.mem, defaults.mem[0], 0),
        idxOrDefault(config.mem, defaults.mem[1], config.mem.length - 1),
    ];
    const _storage = [
        idxOrDefault(config.storage, defaults.storage[0], 0),
        idxOrDefault(config.storage, defaults.storage[1], config.storage.length - 1),
    ];

    const priceSlider   = noUiSlider.create($('.price-range')   as Element, mkSlider(_price,   config.price.length));
    const coresSlider   = noUiSlider.create($('.cores-range')   as Element, mkSlider(_cores,   config.cores.length));
    const memSlider     = noUiSlider.create($('.mem-range')     as Element, mkSlider(_mem,     config.mem.length));
    const storageSlider = noUiSlider.create($('.storage-range') as Element, mkSlider(_storage, config.storage.length));

    function mkSlider(start: number[], max: number) {
        return {
            start,
            connect: true,
            step: 1,
            range: { min: 0, max: max - 1 },
            format: { from: (v: string) => parseInt(v, 10), to: (v: number) => parseInt(String(v), 10) },
        };
    }

    const priceLabel   = $('.label-text-price .label-val')   as HTMLElement | null;
    const coresLabel   = $('.label-text-cores .label-val')   as HTMLElement | null;
    const memLabel     = $('.label-text-mem .label-val')     as HTMLElement | null;
    const storageLabel = $('.label-text-storage .label-val') as HTMLElement | null;

    priceSlider.on('update', (v: number[]) => {
        (priceLabel?.find('.from') as HTMLElement | null)?.text(String(config.price[v[0]!]));
        (priceLabel?.find('.to')   as HTMLElement | null)?.text(String(config.price[v[1]!]));
    });
    coresSlider.on('update', (v: number[]) => {
        (coresLabel?.find('.from') as HTMLElement | null)?.text(String(config.cores[v[0]!]));
        (coresLabel?.find('.to')   as HTMLElement | null)?.text(String(config.cores[v[1]!]));
    });
    memSlider.on('update', (v: number[]) => {
        const from = config.mem[v[0]!]!;
        const to   = config.mem[v[1]!]!;
        (memLabel?.find('.from') as HTMLElement | null)?.html(`<strong>${from}<strong> GB`);
        (memLabel?.find('.to')   as HTMLElement | null)?.html(`<strong>${to}<strong> GB`);
    });
    storageSlider.on('update', (v: number[]) => {
        const from = config.storage[v[0]!]!;
        const to   = config.storage[v[1]!]!;
        (storageLabel?.find('.from') as HTMLElement | null)?.html(
            from >= 1024 ? `<strong>${Math.floor(from / 1024)}<strong> TB` : `<strong>${from}<strong> GB`,
        );
        (storageLabel?.find('.to') as HTMLElement | null)?.html(
            to >= 1024 ? `<strong>${Math.floor(to / 1024)}<strong> TB` : `<strong>${to}<strong> GB`,
        );
    });

    const getCountryName = (loc: string): string => loc === 'nl' ? 'Netherlands' : 'România';

    const priceFormat = (price: string | number): string => {
        if (currency === '€' || currency === '$') return `${currency}${price}`;
        return `${price} ${currency}`;
    };

    const sizeFormat = (size: number): string => {
        const tbSize = Math.round(size / 1024);
        return tbSize >= 1 ? `${tbSize} TB` : `${size} GB`;
    };

    /** Render single server card */
    const getCard = (item: ServerItem): HTMLElement => {
        const cpuCol: HTMLElement[] = item.cpu.map((m) => El('div', { class: 'flex v-center item' },
            El('i', { class: `icon ic-${m.vendor}` }),
            El('div', {},
                El('div', { class: 'name' }, El('strong', {}, m.name), ` - ${m.freq}`),
                El('div', { class: 'info' },
                    m.cores    ? El('span', { class: 'tag' },       String(m.cores))   : null,
                    m.cores    ? ' cores '                                              : null,
                    m.threads  ? El('span', { class: 'tag r-tag' }, String(m.threads)) : null,
                    m.threads  ? ' threads'                                             : null,
                ),
            ),
        ));

        const memCol: HTMLElement[] = item.ram.map((m, i) => {
            const spec: HTMLElement[] = [];
            if (m.freq) spec.push(El('div', { class: 'info' }, El('span', { class: 'tag' }, String(m.freq)), El('strong', {}, ' Mhz')));
            if (m.spec) spec.push(El('div', { class: 'info' }, El('strong', {}, m.spec)));
            return El('div', { class: 'flex v-center item' },
                El('i', { class: `icon ic-mem${i ? '-arrow' : ''}` }),
                El('div', {},
                    El('div', { class: 'name' }, El('strong', {}, String(m.size)), ` GB ${m.type}`),
                    ...spec,
                ),
            );
        });

        const hddCol: HTMLElement[] = item.hdd.map((m) => {
            const mapType: Record<string, [string, string]> = {
                ssd:      ['SSD',          'hdd-ssd'],
                ssd_nvme: ['SSD NVme',     'hdd-nvme'],
                sata:     ['HDD SATA',     'hdd-sata'],
                sas:      ['HDD SAS',      'hdd-sas'],
                cloud:    ['Cloud Storage','hdd-cloud'],
            };
            const [hddType, hddIcon] = mapType[m.type] ?? ['?', 'ssd'];
            return El('div', { class: 'flex v-center item' },
                El('i', { class: `icon ic-${hddIcon}` }),
                El('div', {},
                    El('div', { class: 'name' },
                        Number(m.count) > 1 ? El('span', {}, `${m.count} x `) : null,
                        El('strong', {}, sizeFormat(m.size)),
                    ),
                    El('div', { class: 'info' }, hddType),
                ),
            );
        });

        const netCol: HTMLElement[] = [];
        if (item.net.speed) netCol.push(El('div', { class: 'flex v-center item' },
            El('i', { class: 'icon ic-port' }),
            El('div', {},
                El('div', { class: 'name' }, El('strong', {}, `${item.net.speed} Gbps`)),
                El('div', { class: 'info' }, 'port'),
            ),
        ));
        if (item.net.bw) netCol.push(El('div', { class: 'flex v-center item' },
            El('i', { class: 'icon ic-port-alt' }),
            El('div', {},
                El('div', { class: 'name' }, El('strong', {}, item.net.bw)),
                El('div', { class: 'info' }, 'Bandwidth'),
            ),
        ));

        let optionalRow: HTMLElement | null = null;
        if (item.optional) {
            const optionalCol: HTMLElement[] = [];
            if (item.optional.bgp) optionalCol.push(El('div', { class: 'flex v-center' },
                El('i', { class: 'icon ic-bgp' }),
                El('span', {}, El('strong', {}, 'BGP'), ` ${item.optional.bgp}`),
            ));
            if (item.optional.raid) optionalCol.push(El('div', { class: 'flex v-center' },
                El('i', { class: 'icon ic-raid-alt' }),
                El('span', {}, El('strong', {}, 'RAID'), ` ${item.optional.raid}`),
            ));
            if (item.optional.ipPrice) optionalCol.push(El('div', { class: 'flex v-center' },
                El('i', { class: 'icon ic-ip-addr' }),
                El('strong', {}, locale.ip_addr),
                El('span', { class: 'y-tag' }, `extra ${priceFormat(item.optional.ipPrice)}`),
            ));
            if (item.optional.cpanelPrice) optionalCol.push(El('div', { class: 'flex v-center' },
                El('i', { class: 'icon ic-cpanel' }),
                El('strong', {}, 'cPanel'),
                El('span', { class: 'y-tag' }, ` ${priceFormat(item.optional.cpanelPrice)}`),
            ));
            optionalRow = El('div', { class: 'grid x-between v-center features-bottom' }, ...optionalCol);
        }

        let depCol: HTMLElement | null = null;
        if (item.depTime) {
            let depTime = parseInt(item.depTime, 10);
            let depInt  = locale.depTime.m;
            const h = Math.round(depTime / 60);
            if (h > 0) { depTime = h; depInt = locale.depTime.h; }
            depCol = El('div', { class: 'flex v-center act-time' },
                El('i', { class: 'icon ic-clock' }),
                El('span', {}, El('strong', {}, String(depTime)), ` ${depInt}`),
            );
        }

        let discountCol: HTMLElement | null = null;
        if (item.price.discount) {
            const p = parseFloat(item.price[vars.period] ?? '0');
            const oldPrice = p + (p * parseInt(item.price.discount, 10) / 100);
            discountCol = El('div', { class: 'flex v-center' },
                El('div', { class: 'save' }, `Save ${item.price.discount}%`),
                El('div', { class: 'discount' }, priceFormat(oldPrice.toFixed(2))),
            );
        }

        return El('div', { class: 'card' },
            El('div', { class: 'flex inside' },
                El('div', { class: 'features' },
                    El('div', { class: 'flex v-center title' },
                        El('i', { class: `icon ic-${item.brand}-logo` }),
                        El('span', {}, item.name),
                    ),
                    El('div', { class: 'grid v-center col-group' },
                        El('div', { class: 'col' }, ...cpuCol),
                        El('div', { class: 'col' }, ...memCol),
                        El('div', { class: 'col' }, ...hddCol),
                        El('div', { class: 'col' }, ...netCol),
                    ),
                    optionalRow,
                ),
                El('div', { class: 'order' },
                    El('div', { class: 'flex x-between v-center' },
                        El('div', { class: 'flex v-center country' },
                            El('i', { class: `icon ic-flag ic-flag-${item.location}` }),
                            El('span', {}, getCountryName(item.location)),
                        ),
                        depCol,
                    ),
                    El('div', { class: 'price' },
                        discountCol,
                        El('div', { class: 'actual-price' },
                            El('strong', {}, priceFormat(item.price[vars.period] ?? '0')),
                            ` ${locale.period[vars.period]}`,
                        ),
                    ),
                    El('a', { href: item.orderUrl, class: 'btn-conf' },
                        El('span', {}, locale.config),
                    ),
                ),
            ),
        );
    };

    const counter    = $('.layer-products .number')     as HTMLElement | null;
    const cards      = $('.layer-products .cards')      as HTMLElement | null;
    const pagination = $('.layer-products .pagination') as HTMLElement | null;
    let pgCount = 0;

    const renderList = (): void => {
        let servList = servers.filter((item) => {
            if (!vars.comp_type.includes(item.type)) return false;
            if (vars.dep_type.length === 1 && vars.dep_type.includes('instant')
                && item.depTime && parseInt(item.depTime, 10) > 5) return false;
            if (vars.location.length && !vars.location.includes(item.location)) return false;
            if (vars.period === 'm') {
                const p = parseFloat(item.price.m ?? '0');
                if (p < vars.price[0] || p > vars.price[1]) return false;
            }
            if (!item.cpu.some((m) => m.cores >= vars.cores[0] && m.cores <= vars.cores[1])) return false;
            if (!item.ram.some((m) => m.size  >= vars.mem[0]   && m.size  <= vars.mem[1]))   return false;
            if (!item.hdd.some((m) => m.size  >= vars.storage[0] && m.size <= vars.storage[1])) return false;
            if (vars.hd_type.length && item.cpu[0] && !vars.hd_type.includes(item.cpu[0].vendor)) return false;
            if (vars.inc_gpu && item.gpu !== vars.inc_gpu) return false;
            if (vars.speed.length && item.net.speed && !vars.speed.includes(item.net.speed)) return false;
            if (vars.inc_raid && item.optional && !!item.optional.raid !== vars.inc_raid) return false;
            if (vars.storage_type.length && !item.hdd.some((m) => vars.storage_type.includes(m.type))) return false;
            return true;
        });

        servList = vars.sort === 'a'
            ? servList.sort((a, b) => parseFloat(a.price[vars.period] ?? '0') - parseFloat(b.price[vars.period] ?? '0'))
            : servList.sort((a, b) => parseFloat(b.price[vars.period] ?? '0') - parseFloat(a.price[vars.period] ?? '0'));

        const numRows = servList.length;
        servList = servList.splice((vars.page - 1) * maxRpp, maxRpp);

        if (counter) counter.textContent = String(numRows);
        cards?.html('');
        servList.forEach((item) => cards?.append(getCard(item)));

        pgCount = Math.ceil(numRows / maxRpp);
        pagination?.html('');
        for (let i = 1; i <= pgCount; i++) {
            pagination?.append(El('a', {
                href: '#',
                class: (setPage ? (i === vars.page) : (i === 1)) ? 'active' : 'page',
            }, String(i)));
        }
        setPage = false;
    };

    const updateVars = <K extends keyof FilterVars>(name: K, val: FilterVars[K] | string | number, checked?: boolean): void => {
        if (typeof checked === 'boolean') {
            const arr = vars[name] as unknown as Array<string | number>;
            if (checked) arr.push(val as string | number);
            else (vars[name] as unknown) = arr.filter((itm) => itm !== val);
        } else {
            (vars[name] as unknown) = val;
        }
        renderList();
    };

    const elList = ($('.filter-form') as HTMLFormElement | null)?.elements as unknown as Record<string, HTMLInputElement | NodeListOf<HTMLInputElement>>;
    if (!elList) return;

    const setDefaults = (): void => {
        (elList['comp_type'] as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.comp_type.includes(t.value); });
        (elList['dep_type']  as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.dep_type.includes(t.value); });
        (elList['location']  as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.location.includes(t.value); });
        priceSlider.set(_price);
        for (const t of (elList['period'] as NodeListOf<HTMLInputElement>)) {
            if (defaults.period === t.value) { t.checked = true; break; }
        }
        coresSlider.set(_cores);
        memSlider.set(_mem);
        storageSlider.set(_storage);
        (elList['hd_type'] as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.hd_type.includes(t.value); });
        if (defaults.inc_gpu)  (elList['inc_gpu']  as HTMLInputElement).checked = true;
        (elList['speed'] as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.speed.includes(parseInt(t.value, 10)); });
        if (defaults.inc_raid) (elList['inc_raid'] as HTMLInputElement).checked = true;
        (elList['storage_type'] as NodeListOf<HTMLInputElement>).forEach((t) => { t.checked = defaults.storage_type.includes(t.value); });
        vars = { ...defaults };
        renderList();
    };

    initStickyBookmarks();

    /* Pagination via swipe on cards */
    cards?.on('swiperight', (e: Event) => {
        e.preventDefault();
        if (vars.page > 1) {
            setPage = true;
            updateVars('page', vars.page - 1);
            window.scrollTo({ top: 200, behavior: 'smooth' });
        }
    });
    cards?.on('swipeleft', (e: Event) => {
        e.preventDefault();
        if (vars.page < pgCount) {
            setPage = true;
            updateVars('page', vars.page + 1);
            window.scrollTo({ top: 200, behavior: 'smooth' });
        }
    });

    ($('.reset-filters') as Element | null)?.on('click', setDefaults);
    ($('.layer-products') as Element | null)?.on('change', 'select[name="sort"]', (e: Event) => {
        updateVars('sort', (e.target as HTMLSelectElement).value as 'a' | 'd');
    });

    const onChangeArr = (key: keyof FilterVars) => (t: HTMLInputElement) => {
        t.on('change', (e: Event) => {
            const inp = e.target as HTMLInputElement;
            const v = key === 'speed' ? parseInt(inp.value, 10) : inp.value;
            updateVars(key, v as never, inp.checked);
        });
    };
    (elList['comp_type']    as NodeListOf<HTMLInputElement>).forEach(onChangeArr('comp_type'));
    (elList['dep_type']     as NodeListOf<HTMLInputElement>).forEach(onChangeArr('dep_type'));
    (elList['location']     as NodeListOf<HTMLInputElement>).forEach(onChangeArr('location'));
    (elList['hd_type']      as NodeListOf<HTMLInputElement>).forEach(onChangeArr('hd_type'));
    (elList['speed']        as NodeListOf<HTMLInputElement>).forEach(onChangeArr('speed'));
    (elList['storage_type'] as NodeListOf<HTMLInputElement>).forEach(onChangeArr('storage_type'));

    priceSlider.on('change',   (v: number[]) => updateVars('price',   v.map((n) => config.price[n]!)   as [number, number]));
    coresSlider.on('change',   (v: number[]) => updateVars('cores',   v.map((n) => config.cores[n]!)   as [number, number]));
    memSlider.on('change',     (v: number[]) => updateVars('mem',     v.map((n) => config.mem[n]!)     as [number, number]));
    storageSlider.on('change', (v: number[]) => updateVars('storage', v.map((n) => config.storage[n]!) as [number, number]));

    (elList['period'] as NodeListOf<HTMLInputElement>).forEach((t) => {
        t.on('change', (e: Event) => updateVars('period', (e.target as HTMLInputElement).value as 'm' | 'h'));
    });
    (elList['inc_gpu']  as HTMLInputElement).on('change', (e: Event) => updateVars('inc_gpu',  (e.target as HTMLInputElement).checked));
    (elList['inc_raid'] as HTMLInputElement).on('change', (e: Event) => updateVars('inc_raid', (e.target as HTMLInputElement).checked));

    pagination?.on('click', 'a', function (this: Element, e: Event) {
        e.preventDefault();
        setPage = true;
        updateVars('page', parseInt(this.textContent ?? '1', 10));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    /* Swiper testimonials — PERF-5: defer init until near the viewport */
    initWhenVisible(document.querySelector('.testi-slider'), () => {
        new Swiper('.testi-slider', {
            slidesPerView: 4, speed: 800, spaceBetween: 18, grabCursor: true,
            navigation: { nextEl: '.testi-nav-bar .next', prevEl: '.testi-nav-bar .prev' },
            breakpoints: {
                767:  { slidesPerView: 1, spaceBetween: 18 },
                1023: { slidesPerView: 2, spaceBetween: 18 },
            },
        });
    });

    /* Mobile filter popup */
    const filterForm = $('.layer-products .left-side') as HTMLElement | null;
    ($('.layer-products') as Element | null)?.on('click', '.btn-filter', () => {
        if (!filterForm) return;
        document.body.style.overflowY = 'hidden';
        filterForm.style.display = 'block';
        setTimeout(() => filterForm.classList.add('opened'));
    });
    ($('.layer-products') as Element | null)?.on('click', '.mob-filter-close, .btn-close', () => {
        if (!filterForm) return;
        document.body.style.overflowY = 'auto';
        filterForm.addEventListener('transitionend', () => { filterForm.style.display = 'none'; }, { once: true });
        setTimeout(() => filterForm.classList.remove('opened'));
    });

    /* Fetch + parse XML data */
    fetch(`/res/servers.xml?${Date.now()}`, { method: 'get', cache: 'no-cache' })
        .then((r) => r.text())
        .then((res) => {
            const xml = new DOMParser().parseFromString(res, 'text/xml');
            const nav = xml.find('nav') as Element | null;
            if (!nav) return;
            const text = nav.find(`text[lang="${defaultLang}"]`) as Element | null;
            if (!text) return;

            currency = (nav.find('currency') as Element | null)?.textContent ?? '';
            const txtPeriod = text.find('period') as Element | null;
            const txtDep    = text.find('dep_time') as Element | null;

            locale = {
                period: {
                    m: (txtPeriod?.find('monthly') as Element | null)?.textContent ?? '',
                    h: (txtPeriod?.find('hourly')  as Element | null)?.textContent ?? '',
                },
                included: (text.find('included') as Element | null)?.textContent ?? '',
                ip_addr:  (text.find('ip_addr')  as Element | null)?.textContent ?? '',
                depTime: {
                    m: (txtDep?.find('minutes') as Element | null)?.textContent ?? '',
                    h: (txtDep?.find('hours')   as Element | null)?.textContent ?? '',
                },
                config: (text.find('config') as Element | null)?.textContent ?? '',
            };

            (nav.find('server', true) as NodeListOf<Element>).forEach((t) => {
                const cpu: ServerItem['cpu'] = [];
                const cpuNode = t.find('cpu') as Element | null;
                cpuNode?.find('model', true).forEach((m: Element) => {
                    const model: ServerItem['cpu'][number] = {
                        vendor: (m.find('vendor') as Element).textContent ?? '',
                        name:   (m.find('name')   as Element).textContent ?? '',
                        freq:   (m.find('freq')   as Element).textContent ?? '',
                        cores:  parseInt((m.find('cores') as Element).textContent ?? '0', 10),
                    };
                    const threads = m.find('threads') as Element | null;
                    if (threads) model.threads = parseInt(threads.textContent ?? '0', 10);
                    cpu.push(model);
                });

                const ram: ServerItem['ram'] = [];
                const ramNode = t.find('ram') as Element | null;
                ramNode?.find('model', true).forEach((m: Element) => {
                    const model: ServerItem['ram'][number] = {
                        size: parseInt((m.find('size') as Element).textContent ?? '0', 10),
                        type: (m.find('type') as Element).textContent ?? '',
                    };
                    const freq = m.find('freq') as Element | null;
                    if (freq) model.freq = parseInt(freq.textContent ?? '0', 10);
                    const spec = m.find('spec') as Element | null;
                    if (spec) model.spec = spec.textContent ?? '';
                    ram.push(model);
                });

                const hdd: ServerItem['hdd'] = [];
                const hddNode = t.find('hdd') as Element | null;
                hddNode?.find('model', true).forEach((m: Element) => {
                    const driveCount = m.find('count') as Element | null;
                    hdd.push({
                        size:  parseInt((m.find('size') as Element).textContent ?? '0', 10),
                        type:  (m.find('type') as Element).textContent ?? '',
                        count: driveCount ? (driveCount.textContent ?? '1') : 1,
                    });
                });

                const net: ServerItem['net'] = {};
                const netNode = t.find('net') as Element | null;
                if (netNode) {
                    const sp = netNode.find('speed') as Element | null;
                    if (sp) net.speed = parseInt(sp.textContent ?? '0', 10);
                    const bw = netNode.find('bw') as Element | null;
                    if (bw) net.bw = bw.textContent ?? '';
                }

                const price: ServerItem['price'] = {};
                const priceNode = t.find('price') as Element | null;
                if (priceNode) {
                    const m = priceNode.find('monthly')  as Element | null; if (m) price.m = m.textContent ?? '';
                    const h = priceNode.find('hourly')   as Element | null; if (h) price.h = h.textContent ?? '';
                    const d = priceNode.find('discount') as Element | null; if (d) price.discount = d.textContent ?? '';
                }

                const depNode  = t.find('dep_time')  as Element | null;
                const orderUrl = t.find('order_url') as Element | null;
                const item: ServerItem = {
                    type:     t.getAttribute('type')     ?? '',
                    brand:    t.getAttribute('brand')    ?? '',
                    location: t.getAttribute('location') ?? '',
                    name:     ((t.find('name') as Element | null)?.textContent) ?? '',
                    cpu, ram, hdd, net, price,
                    gpu:      t.getAttribute('gpu') === '1',
                    orderUrl: orderUrl ? (orderUrl.textContent ?? '#') : '#',
                };
                if (depNode) item.depTime = depNode.textContent ?? '';

                const optNode = t.find('optional') as Element | null;
                if (optNode) {
                    item.optional = {};
                    const ipPrice = optNode.find('ip_price') as Element | null;
                    if (ipPrice) item.optional.ipPrice = ipPrice.textContent ?? '';
                    const cpanelPrice = optNode.find('cpanel_price') as Element | null;
                    if (cpanelPrice) item.optional.cpanelPrice = cpanelPrice.textContent ?? '';
                    const raid = optNode.find('raid') as Element | null;
                    const bgp  = optNode.find('bgp')  as Element | null;
                    item.optional.raid = raid ? (raid.textContent ?? '') : false;
                    item.optional.bgp  = bgp  ? (bgp.textContent  ?? '') : false;
                }

                servers.push(item);
            });

            setDefaults();
        })
        .catch((e) => { if (import.meta.env.DEV) console.error('[servers]', e); });
});
